import { useState } from "react";
import { Send, MapPin, Phone, Mail, Linkedin, Twitter, CheckCircle } from "lucide-react";
import axios from "axios";
import { useTranslation } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = ({ settings }) => {
  const { t } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [contactStatus, setContactStatus] = useState(null);
  const [loading, setLoading] = useState({ newsletter: false, contact: false });

  const handleNewsletter = async (e) => {
    e.preventDefault();
    setLoading((prev) => ({ ...prev, newsletter: true }));
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      setNewsletterStatus("success");
      setNewsletterEmail("");
    } catch (err) {
      setNewsletterStatus(err.response?.data?.detail || "error");
    }
    setLoading((prev) => ({ ...prev, newsletter: false }));
  };

  const handleContact = async (e) => {
    e.preventDefault();
    setLoading((prev) => ({ ...prev, contact: true }));
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactStatus("success");
      setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setContactStatus("error");
    }
    setLoading((prev) => ({ ...prev, contact: false }));
  };

  return (
    <section id="contact" className="py-24 bg-[#131B2F]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-[#00CCC2] mb-4">{t("newsletter_label")}</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              <span className="gradient-text">{t("newsletter_title")}</span>
            </h2>
            <p className="text-slate-400 mb-8">{t("newsletter_subtitle")}</p>
            
            <form onSubmit={handleNewsletter} className="flex gap-4 mb-12">
              <input type="email" value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder={t("newsletter_placeholder")} required
                className="flex-1 px-6 py-4 rounded-full bg-[#0B1121] border border-white/10 focus:border-[#2395E3] outline-none transition-colors"
                data-testid="newsletter-email" />
              <button type="submit" disabled={loading.newsletter}
                className="px-8 py-4 btn-primary rounded-full flex items-center gap-2" data-testid="newsletter-submit">
                {loading.newsletter ? "..." : <><Send className="w-5 h-5" /> {t("newsletter_submit")}</>}
              </button>
            </form>
            {newsletterStatus === "success" && (
              <div className="flex items-center gap-2 text-green-400 mb-8" data-testid="newsletter-success">
                <CheckCircle className="w-5 h-5" />{t("newsletter_success")}
              </div>
            )}
            {newsletterStatus && newsletterStatus !== "success" && (
              <p className="text-red-400 mb-8">{newsletterStatus}</p>
            )}

            <div className="space-y-6">
              <h3 className="text-xl font-semibold">{t("contact_coords")}</h3>
              <div className="space-y-4">
                {settings?.address && (
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-[#0B1121]"><MapPin className="w-5 h-5 text-[#00CCC2]" /></div>
                    <span className="text-slate-300">{settings.address}</span>
                  </div>
                )}
                {settings?.phone && (
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-[#0B1121]"><Phone className="w-5 h-5 text-[#00CCC2]" /></div>
                    <span className="text-slate-300">{settings.phone}</span>
                  </div>
                )}
                {settings?.email && (
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-[#0B1121]"><Mail className="w-5 h-5 text-[#00CCC2]" /></div>
                    <span className="text-slate-300">{settings.email}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-4 pt-4">
                {settings?.linkedin && <a href={settings.linkedin} className="p-3 rounded-xl bg-[#0B1121] hover:bg-[#2395E3]/20 transition-colors"><Linkedin className="w-5 h-5" /></a>}
                {settings?.twitter && <a href={settings.twitter} className="p-3 rounded-xl bg-[#0B1121] hover:bg-[#2395E3]/20 transition-colors"><Twitter className="w-5 h-5" /></a>}
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-[#00CCC2] mb-4">{t("contact_label")}</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">
              <span className="gradient-text">{t("contact_title")}</span>
            </h2>
            
            {contactStatus === "success" ? (
              <div className="p-8 rounded-2xl bg-[#0B1121] border border-green-500/30 text-center" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">{t("contact_success_title")}</h3>
                <p className="text-slate-400">{t("contact_success_msg")}</p>
              </div>
            ) : (
              <form onSubmit={handleContact} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <input type="text" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder={t("contact_name")} required className="px-6 py-4 rounded-xl bg-[#0B1121] border border-white/10 focus:border-[#2395E3] outline-none transition-colors" data-testid="contact-name" />
                  <input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder={t("contact_email")} required className="px-6 py-4 rounded-xl bg-[#0B1121] border border-white/10 focus:border-[#2395E3] outline-none transition-colors" data-testid="contact-email" />
                </div>
                <input type="text" value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  placeholder={t("contact_subject")} required className="w-full px-6 py-4 rounded-xl bg-[#0B1121] border border-white/10 focus:border-[#2395E3] outline-none transition-colors" data-testid="contact-subject" />
                <textarea value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder={t("contact_message")} required rows={5} className="w-full px-6 py-4 rounded-xl bg-[#0B1121] border border-white/10 focus:border-[#2395E3] outline-none transition-colors resize-none" data-testid="contact-message" />
                <button type="submit" disabled={loading.contact}
                  className="w-full px-8 py-4 btn-primary rounded-xl flex items-center justify-center gap-2 text-lg" data-testid="contact-submit">
                  {loading.contact ? t("contact_sending") : <><Send className="w-5 h-5" /> {t("contact_submit")}</>}
                </button>
                {contactStatus === "error" && <p className="text-red-400 text-center">{t("contact_error")}</p>}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
